import _defineProperty from '@babel/runtime/helpers/defineProperty';
import nacl from '@toruslabs/tweetnacl-js';
import base58 from 'bs58';
import nodeCrypto from 'crypto';
import { isUri } from 'valid-url';

const DOMAIN = "(?<domain>([^?#]*)) wants you to sign in with your Solana account:";
const ADDRESS = "\\n(?<address>[a-zA-Z0-9]{32,44})\\n\\n";
const STATEMENT = "((?<statement>[^\\n]+)\\n)?";
const URI = "(([^:?#]+):)?(([^?#]*))?([^?#]*)(\\?([^#]*))?(#(.*))";
const URI_LINE = `\\nURI: (?<uri>${URI}?)`;
const VERSION = "\\nVersion: (?<version>1)";
const CHAIN_ID = "\\nChain ID: (?<chainId>[0-9]+)";
const NONCE = "\\nNonce: (?<nonce>[a-zA-Z0-9]{8,})";
const DATETIME = `([0-9]+)-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])[Tt]([01][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9]|60)(.[0-9]+)?(([Zz])|([+|-]([01][0-9]|2[0-3]):[0-5][0-9]))`;
const ISSUED_AT = `\\nIssued At: (?<issuedAt>${DATETIME})`;
const EXPIRATION_TIME = `(\\nExpiration Time: (?<expirationTime>${DATETIME}))?`;
const NOT_BEFORE = `(\\nNot Before: (?<notBefore>${DATETIME}))?`;
const REQUEST_ID = "(\\nRequest ID: (?<requestId>[-._~!$&'()*+,;=:@%a-zA-Z0-9]*))?";
const RESOURCES = `(\\nResources:(?<resources>(\\n- ${URI}?)+))?`;
const MESSAGE = `^${DOMAIN}${ADDRESS}${STATEMENT}${URI_LINE}${VERSION}${CHAIN_ID}${NONCE}${ISSUED_AT}${EXPIRATION_TIME}${NOT_BEFORE}${REQUEST_ID}${RESOURCES}$`;
class ParsedMessage {
  constructor(msg) {
    var _match$groups, _match$groups2, _match$groups3, _match$groups4, _match$groups5, _match$groups6, _match$groups7, _match$groups8, _match$groups9, _match$groups10, _match$groups11, _match$groups12;
    _defineProperty(this, "domain", void 0);
    _defineProperty(this, "address", void 0);
    _defineProperty(this, "statement", void 0);
    _defineProperty(this, "uri", void 0);
    _defineProperty(this, "version", void 0);
    _defineProperty(this, "chainId", void 0);
    _defineProperty(this, "nonce", void 0);
    _defineProperty(this, "issuedAt", void 0);
    _defineProperty(this, "expirationTime", void 0);
    _defineProperty(this, "notBefore", void 0);
    _defineProperty(this, "requestId", void 0);
    _defineProperty(this, "resources", void 0);
    _defineProperty(this, "match", void 0);
    const REGEX = new RegExp(MESSAGE, "g");
    const match = REGEX.exec(msg);
    if (!match) {
      throw new Error("Message did not match the regular expression.");
    }
    this.match = match;
    this.domain = match === null || match === void 0 || (_match$groups = match.groups) === null || _match$groups === void 0 ? void 0 : _match$groups.domain;
    this.address = match === null || match === void 0 || (_match$groups2 = match.groups) === null || _match$groups2 === void 0 ? void 0 : _match$groups2.address;
    this.statement = match === null || match === void 0 || (_match$groups3 = match.groups) === null || _match$groups3 === void 0 ? void 0 : _match$groups3.statement;
    this.uri = match === null || match === void 0 || (_match$groups4 = match.groups) === null || _match$groups4 === void 0 ? void 0 : _match$groups4.uri;
    this.version = match === null || match === void 0 || (_match$groups5 = match.groups) === null || _match$groups5 === void 0 ? void 0 : _match$groups5.version;
    this.nonce = match === null || match === void 0 || (_match$groups6 = match.groups) === null || _match$groups6 === void 0 ? void 0 : _match$groups6.nonce;
    this.chainId = parseInt(match === null || match === void 0 || (_match$groups7 = match.groups) === null || _match$groups7 === void 0 ? void 0 : _match$groups7.chainId);
    this.issuedAt = match === null || match === void 0 || (_match$groups8 = match.groups) === null || _match$groups8 === void 0 ? void 0 : _match$groups8.issuedAt;
    this.expirationTime = match === null || match === void 0 || (_match$groups9 = match.groups) === null || _match$groups9 === void 0 ? void 0 : _match$groups9.expirationTime;
    this.notBefore = match === null || match === void 0 || (_match$groups10 = match.groups) === null || _match$groups10 === void 0 ? void 0 : _match$groups10.notBefore;
    this.requestId = match === null || match === void 0 || (_match$groups11 = match.groups) === null || _match$groups11 === void 0 ? void 0 : _match$groups11.requestId;
    this.resources = match === null || match === void 0 || (_match$groups12 = match.groups) === null || _match$groups12 === void 0 || (_match$groups12 = _match$groups12.resources) === null || _match$groups12 === void 0 ? void 0 : _match$groups12.split("\n- ").slice(1);
  }
}

class SignatureMeta {}
class Signature {
  constructor() {
    _defineProperty(this, "t", void 0);
    // signature scheme
    _defineProperty(this, "m", void 0);
    // signature related metadata (optional)
    _defineProperty(this, "s", void 0);
  } // signature
}
class Header {
  constructor() {
    /** specifies format of the payload. */
    _defineProperty(this, "t", void 0);
  }
}
class Payload {
  constructor() {
    /** RFC 4501 dns authority that is requesting the signing. */
    _defineProperty(this, "domain", void 0);
    /** Solana address performing the signing */
    _defineProperty(this, "address", void 0);
    /** Human-readable ASCII assertion that the user will sign, and it must not contain newline characters. */
    _defineProperty(this, "statement", void 0);
    /** RFC 3986 URI referring to the resource that is the subject of the signing
     *  (as in the __subject__ of a claim). */
    _defineProperty(this, "uri", void 0);
    /** Current version of the message. */
    _defineProperty(this, "version", void 0);
    /** Chain ID to which the session is bound, and the network where
     * Contract Accounts must be resolved. */
    _defineProperty(this, "chainId", void 0);
    /** Randomized token used to prevent replay attacks, at least 8 alphanumeric
     * characters. */
    _defineProperty(this, "nonce", void 0);
    /** ISO 8601 datetime string of the current time. */
    _defineProperty(this, "issuedAt", void 0);
    /** ISO 8601 datetime string that, if present, indicates when the signed
     * authentication message is no longer valid. */
    _defineProperty(this, "expirationTime", void 0);
    /** ISO 8601 datetime string that, if present, indicates when the signed
     * authentication message will become valid. */
    _defineProperty(this, "notBefore", void 0);
    /** System-specific identifier that may be used to uniquely refer to the
     * sign-in request. */
    _defineProperty(this, "requestId", void 0);
    /** List of information or references to information the user wishes to have
     * resolved as part of authentication by the relying party. They are
     * expressed as RFC 3986 URIs separated by `\n- `. */
    _defineProperty(this, "resources", void 0);
  }
}
let ErrorTypes = /*#__PURE__*/function (ErrorTypes) {
  ErrorTypes["EXPIRED_MESSAGE"] = "Expired message.";
  ErrorTypes["INVALID_DOMAIN"] = "Invalid domain.";
  ErrorTypes["DOMAIN_MISMATCH"] = "Domain do not match provided domain for verification.";
  ErrorTypes["NONCE_MISMATCH"] = "Nonce do not match provided nonce for verification.";
  ErrorTypes["INVALID_ADDRESS"] = "Invalid address.";
  ErrorTypes["INVALID_URI"] = "URI does not conform to RFC 3986.";
  ErrorTypes["INVALID_NONCE"] = "Nonce size smaller then 8 characters or is not alphanumeric.";
  ErrorTypes["NOT_YET_VALID_MESSAGE"] = "Message is not valid yet.";
  ErrorTypes["INVALID_SIGNATURE"] = "Signature do not match address of the message.";
  ErrorTypes["INVALID_TIME_FORMAT"] = "Invalid time format.";
  ErrorTypes["INVALID_MESSAGE_VERSION"] = "Invalid message version.";
  ErrorTypes["UNABLE_TO_PARSE"] = "Unable to parse the message.";
  ErrorTypes["MALFORMED_SESSION"] = "Malformed Session";
  return ErrorTypes;
}({});

/**
 * Interface used to return errors in Responses.
 */
class SignInWithSolanaError extends Error {
  constructor(type, expected, received) {
    super(type);
    /** Type of the error. */
    _defineProperty(this, "type", void 0);
    /** Expected value or condition to pass. */
    _defineProperty(this, "expected", void 0);
    /** Received value that caused the failure. */
    _defineProperty(this, "received", void 0);
    this.type = type;
    this.expected = expected;
    this.received = received;
  }
}

/**
 * Returned on verifications.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const browserCrypto = global.crypto || global.msCrypto || {};
function randomBytes(size) {
  const arr = new Uint8Array(size);
  if (typeof browserCrypto.getRandomValues === "undefined") {
    return Buffer.from(nodeCrypto.randomBytes(size));
  }
  browserCrypto.getRandomValues(arr);
  return Buffer.from(arr);
}
class SIWS {
  /**
   * Creates a parsed Sign-In with Solana Message object from a
   * string or an object. If a string is used an parser is called to
   * validate the parameter, otherwise the fields are attributed.
   * @param param - Sign message as a string or an object.
   */
  constructor(param) {
    _defineProperty(this, "header", void 0);
    _defineProperty(this, "payload", void 0);
    _defineProperty(this, "signature", void 0);
    if (typeof param === "string") {
      const parsedMessage = new ParsedMessage(param);
      this.payload = {
        domain: parsedMessage.domain,
        address: parsedMessage.address,
        statement: parsedMessage.statement,
        uri: parsedMessage.uri,
        version: parsedMessage.version,
        nonce: parsedMessage.nonce,
        issuedAt: parsedMessage.issuedAt,
        expirationTime: parsedMessage.expirationTime,
        notBefore: parsedMessage.notBefore,
        requestId: parsedMessage.requestId,
        chainId: parsedMessage.chainId,
        resources: parsedMessage.resources
      };
    } else {
      Object.assign(this, param);
      if (typeof this.payload.chainId === "string") {
        this.payload.chainId = parseInt(this.payload.chainId);
      }
      if (!this.payload.nonce) {
        this.payload.nonce = randomBytes(8).toString("hex");
      }
    }
  }

  /**
   * This function can be used to retrieve a formated message for
   * signature, although you can call it directly it's advised to use
   * [prepareMessage()] instead which will resolve to the correct method based
   * on the [type] attribute of this object, in case of other formats being
   * implemented.
   * @returns - message
   */
  toMessage() {
    /** Validates all fields of the object */

    this.validate();
    const header = `${this.payload.domain} wants you to sign in with your Solana account:`;
    const uriField = `URI: ${this.payload.uri}`;
    let prefix = [header, this.payload.address].join("\n");
    const versionField = `Version: ${this.payload.version}`;
    const chainField = `Chain ID: ${this.payload.chainId || "1"}`;
    const nonceField = `Nonce: ${this.payload.nonce}`;
    const suffixArray = [uriField, versionField, chainField, nonceField];
    if (this.payload.issuedAt) {
      Date.parse(this.payload.issuedAt);
    }
    this.payload.issuedAt = this.payload.issuedAt ? this.payload.issuedAt : new Date().toISOString();
    suffixArray.push(`Issued At: ${this.payload.issuedAt}`);
    if (this.payload.expirationTime) {
      const expiryField = `Expiration Time: ${this.payload.expirationTime}`;
      suffixArray.push(expiryField);
    }
    if (this.payload.notBefore) {
      suffixArray.push(`Not Before: ${this.payload.notBefore}`);
    }
    if (this.payload.requestId) {
      suffixArray.push(`Request ID: ${this.payload.requestId}`);
    }
    if (this.payload.resources) {
      suffixArray.push([`Resources:`, ...this.payload.resources.map(x => `- ${x}`)].join("\n"));
    }
    const suffix = suffixArray.join("\n");
    prefix = [prefix, this.payload.statement].join("\n\n");
    if (this.payload.statement) {
      prefix += "\n";
    }
    return [prefix, suffix].join("\n");
  }

  /**
   * This method parses all the fields in the object and creates a sign
   * message according with the type defined.
   * @returns Returns a message ready to be signed according with the
   * type defined in the object.
   */
  prepareMessage() {
    let message;
    switch (this.payload.version) {
      case "1":
        {
          message = this.toMessage();
          break;
        }
      default:
        {
          message = this.toMessage();
          break;
        }
    }
    return message;
  }

  /**
   * Validates the value of this object fields.
   * @throws Throws an ErrorType if a field is invalid.
   */
  validate() {
    /** `domain` check. */
    if (this.payload.domain.length === 0 || !/[^#?]*/.test(this.payload.domain)) {
      throw new SignInWithSolanaError(ErrorTypes.INVALID_DOMAIN, `${this.payload.domain} to be a valid domain.`);
    }

    /** Check if the URI is valid. */
    if (!isUri(this.payload.uri)) {
      throw new SignInWithSolanaError(ErrorTypes.INVALID_URI, `${this.payload.uri} to be a valid uri.`);
    }

    /** Check if the version is 1. */
    if (this.payload.version !== "1") {
      throw new SignInWithSolanaError(ErrorTypes.INVALID_MESSAGE_VERSION, "1", this.payload.version);
    }

    /** Check if the nonce is alphanumeric and bigger then 8 characters */
    const nonce = this.payload.nonce.match(/[a-zA-Z0-9]{8,}/);
    if (!nonce || this.payload.nonce.length < 8 || nonce[0] !== this.payload.nonce) {
      throw new SignInWithSolanaError(ErrorTypes.INVALID_NONCE, `Length > 8 (${nonce.length}). Alphanumeric.`, this.payload.nonce);
    }
    const ISO8601 = /([0-9]+)-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])[Tt]([01][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9]|60)(\.[0-9]+)?(([Zz])|([+|-]([01][0-9]|2[0-3]):[0-5][0-9]))/;
    /** `issuedAt` conforms to ISO-8601 */
    if (this.payload.issuedAt) {
      if (!ISO8601.test(this.payload.issuedAt)) {
        throw new Error(ErrorTypes.INVALID_TIME_FORMAT);
      }
    }

    /** `expirationTime` conforms to ISO-8601 */
    if (this.payload.expirationTime) {
      if (!ISO8601.test(this.payload.expirationTime)) {
        throw new Error(ErrorTypes.INVALID_TIME_FORMAT);
      }
    }

    /** `notBefore` conforms to ISO-8601 */
    if (this.payload.notBefore) {
      if (!ISO8601.test(this.payload.notBefore)) {
        throw new Error(ErrorTypes.INVALID_TIME_FORMAT);
      }
    }
  }

  /**
   * Validates the integrity of the object by matching it's signature.
   * @param params - Parameters to verify the integrity of the message, signature is required.
   * @returns This object if valid.
   */
  async verify(params) {
    return new Promise(resolve => {
      const {
        payload,
        signature
      } = params;

      /** Domain binding */
      if (payload.domain && payload.domain !== this.payload.domain) {
        resolve({
          success: false,
          data: this,
          error: new SignInWithSolanaError(ErrorTypes.DOMAIN_MISMATCH, payload.domain, this.payload.domain)
        });
      }

      /** Nonce binding */
      if (payload.nonce && payload.nonce !== this.payload.nonce) {
        resolve({
          success: false,
          data: this,
          error: new SignInWithSolanaError(ErrorTypes.NONCE_MISMATCH, payload.nonce, this.payload.nonce)
        });
      }

      /** Check time */
      const checkTime = new Date();

      /** Expiry Checks */
      if (this.payload.expirationTime) {
        const expirationDate = new Date(this.payload.expirationTime);

        // Check if the message hasn't expired
        if (checkTime.getTime() >= expirationDate.getTime()) {
          resolve({
            success: false,
            data: this,
            error: new SignInWithSolanaError(ErrorTypes.EXPIRED_MESSAGE, `${checkTime.toISOString()} < ${expirationDate.toISOString()}`, `${checkTime.toISOString()} >= ${expirationDate.toISOString()}`)
          });
        }
      }

      /** Message is valid already */
      if (this.payload.notBefore) {
        const notBefore = new Date(this.payload.notBefore);
        if (checkTime.getTime() < notBefore.getTime()) {
          resolve({
            success: false,
            data: this,
            error: new SignInWithSolanaError(ErrorTypes.EXPIRED_MESSAGE, `${checkTime.toISOString()} >= ${notBefore.toISOString()}`, `${checkTime.toISOString()} < ${notBefore.toISOString()}`)
          });
        }
      }
      const message = this.prepareMessage();

      // Verification
      const encodedMessage = new TextEncoder().encode(message);
      const data = nacl.sign.detached.verify(encodedMessage, base58.decode(signature.s), base58.decode(payload.address));
      if (!data) resolve({
        success: false,
        data: this,
        error: new SignInWithSolanaError(ErrorTypes.INVALID_SIGNATURE, "Signature verfication failed")
      });
      resolve({
        success: true,
        data: this
      });
    });
  }
}

export { ErrorTypes, Header, Payload, SIWS, SignInWithSolanaError, Signature, SignatureMeta };
